import React, { useCallback, useRef, useState } from "react";
import { AlertCircle, CheckCircle2, FileText, Loader2, Upload } from "lucide-react";
import { validateIntakeFile } from "../../lib/validateIntakeFile";
import { documentAPI } from "../../services/api";

/**
 * High-security intake drop zone for one document stream.
 * Validates file types locally, uploads via multipart, and returns a hosted URL.
 */
export default function DocumentDropZone({
  stream,
  title,
  subtitle,
  accept,
  uploadUrl,
  fileName,
  onUploadComplete,
  onError,
}) {
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFile = useCallback(
    async (file) => {
      const result = validateIntakeFile(file, stream);
      if (!result.valid) {
        setLocalError(result.error);
        onError?.(result.error);
        return;
      }

      setLocalError(null);
      setIsUploading(true);
      try {
        const uploaded = await documentAPI.uploadDocument(file);
        onUploadComplete?.({
          url: uploaded.url,
          fileName: uploaded.fileName ?? file.name,
        });
      } catch (err) {
        const message = err?.message ?? "Upload failed";
        setLocalError(message);
        onError?.(message);
      } finally {
        setIsUploading(false);
      }
    },
    [stream, onError, onUploadComplete]
  );

  const onDrop = (event) => {
    event.preventDefault();
    setDragActive(false);
    const file = event.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  };

  const onBrowse = (event) => {
    const file = event.target.files?.[0];
    if (file) void handleFile(file);
    event.target.value = "";
  };

  const isReady = Boolean(uploadUrl?.trim());

  return (
    <div className="flex flex-col gap-3">
      <div>
        <p className="text-sm font-semibold tracking-wide text-slate-100 uppercase">{title}</p>
        <p className="text-xs text-slate-400 mt-1">{subtitle}</p>
      </div>

      <div
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") inputRef.current?.click();
        }}
        onClick={() => !isUploading && inputRef.current?.click()}
        onDragEnter={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setDragActive(false);
        }}
        onDrop={onDrop}
        className={[
          "relative cursor-pointer border-2 border-dashed px-5 py-8 transition-colors",
          isUploading
            ? "border-cyan-400/60 bg-cyan-950/20 cursor-wait"
            : dragActive
            ? "border-cyan-400 bg-cyan-950/40"
            : isReady
            ? "border-emerald-500/60 bg-emerald-950/20"
            : "border-slate-600 bg-slate-900/60 hover:border-slate-500",
        ].join(" ")}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          disabled={isUploading}
          onChange={onBrowse}
        />
        <div className="flex flex-col items-center text-center gap-3">
          {isUploading ? (
            <Loader2 className="h-8 w-8 text-cyan-400 animate-spin" />
          ) : isReady ? (
            <CheckCircle2 className="h-8 w-8 text-emerald-400" />
          ) : (
            <Upload className="h-8 w-8 text-slate-400" />
          )}
          <div>
            <p className="text-sm font-medium text-slate-200">
              {isUploading
                ? "Uploading securely..."
                : isReady
                ? fileName || "Document ready"
                : "Drop file here or click to browse"}
            </p>
            <p className="text-xs text-slate-500 mt-1 flex items-center justify-center gap-1">
              <FileText className="h-3.5 w-3.5" />
              {stream === "assessment" ? "PDF only" : "PDF or CSV"}
            </p>
          </div>
        </div>
      </div>

      {localError && (
        <div className="flex items-start gap-2 border border-red-500/40 bg-red-950/30 px-3 py-2 text-xs text-red-300">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>{localError}</span>
        </div>
      )}
    </div>
  );
}
